import React, { useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { Button, EmptyPlaceholder, InputField } from '@components/common'
import { IContact } from '@dataTypes/contact.type'
import { RecipientDestination } from '@dataTypes/fax.type'
import { getFullName } from '@utils/fullname.util'

type Props = {
  show: boolean
  contacts: IContact[]
  currentContact?: IContact | null
  currentDestination?: RecipientDestination
  onHide: () => void
  onSelect: (contact: IContact, destination: RecipientDestination) => void
}

const destinationOptions = (
  contact: IContact,
): { value: RecipientDestination; label: string }[] => {
  const options: { value: RecipientDestination; label: string }[] = []
  if (contact.fax_number?.trim()) {
    options.push({ value: 'fax_number', label: `Fax: ${contact.fax_number}` })
  }
  if (contact.email?.trim()) {
    options.push({ value: 'email', label: `Email: ${contact.email}` })
  }
  return options
}

const SelectFaxRecipientModal: React.FC<Props> = ({
  show,
  contacts,
  currentContact,
  currentDestination,
  onHide,
  onSelect,
}) => {
  const [query, setQuery] = useState('')
  const [selectedContact, setSelectedContact] = useState<IContact | null>(null)
  const [destination, setDestination] =
    useState<RecipientDestination>('fax_number')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return contacts.filter((c) => {
      const hasChannel = c.fax_number?.trim() || c.email?.trim()
      if (!hasChannel) return false
      if (!q) return true
      const name = getFullName(c).toLowerCase()
      return (
        name.includes(q) ||
        (c.fax_number ?? '').toLowerCase().includes(q) ||
        (c.email ?? '').toLowerCase().includes(q)
      )
    })
  }, [contacts, query])

  const options = useMemo(
    () => (selectedContact ? destinationOptions(selectedContact) : []),
    [selectedContact],
  )

  useEffect(() => {
    if (!show) return
    setQuery('')
    setSelectedContact(currentContact ?? null)
    const opts = currentContact ? destinationOptions(currentContact) : []
    const preferred =
      opts.find((o) => o.value === currentDestination)?.value ??
      opts[0]?.value ??
      'fax_number'
    setDestination(preferred)
  }, [show, currentContact, currentDestination])

  const handleSelectContact = (contact: IContact): void => {
    setSelectedContact(contact)
    const opts = destinationOptions(contact)
    setDestination(opts[0]?.value ?? 'fax_number')
  }

  const onDone = (): void => {
    if (selectedContact) {
      onSelect(selectedContact, destination)
      onHide()
    }
  }

  return (
    <Modal show={show} onHide={onHide} centered className="fax-recipient-modal">
      <Modal.Header closeButton>
        <Modal.Title>Select fax recipient</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {filtered.length === 0 ? (
          <EmptyPlaceholder
            title="No contacts found"
            content="No available contacts with a fax number."
            icon="mgc_user_3_fill"
          />
        ) : (
          <>
            <InputField
              label="Search contact"
              placeholder="Search contact"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="fax-recipient-list">
              {filtered.map((contact) => (
                <div
                  key={contact.contact_id}
                  className={`fax-recipient-row ${selectedContact?.contact_id === contact.contact_id ? 'fax-selected-recipient' : ''}`}
                  role="button"
                  tabIndex={0}
                  aria-label={getFullName(contact)}
                  onClick={() => handleSelectContact(contact)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return
                    e.preventDefault()
                    handleSelectContact(contact)
                  }}
                >
                  <span className="fax-recipient-avatar mgc_user_3_fill" />
                  <div>
                    <div className="fax-recipient-name">
                      {getFullName(contact)}
                    </div>
                    <div>{contact.fax_number}</div>
                    <div>{contact.email}</div>
                  </div>
                </div>
              ))}
            </div>
            {selectedContact && options.length > 1 && (
              <div className="fax-recipient-destination">
                <span className="fax-recipient-destination-label">Send to</span>
                {options.map((opt) => (
                  <label
                    key={opt.value}
                    className="fax-recipient-destination-option"
                  >
                    <input
                      type="radio"
                      name="faxDestination"
                      checked={destination === opt.value}
                      onChange={() => setDestination(opt.value)}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button className="button-secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          className="button-primary"
          disabled={!selectedContact}
          onClick={onDone}
        >
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default SelectFaxRecipientModal
